import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { throwError } from 'rxjs';

export interface Prompt {
  id: number;
  prompt_text: string;
  user_id: number | null;
  status: number;
  created_at: string;
  updated_at: string;
}

export interface AdminUser {
  user_id: number;
  user_name: string;
  full_name: string;
}

export interface PromptResponse {
  success: boolean;
  data: Prompt | Prompt[] | AdminUser[];
  message?: string;
  count?: number;
  currentPage?: number;
  lastPage?: number;
  perPage?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PromptService {
  private apiUrl = `${environment.api}prompts`;
  private promptsSubject = new BehaviorSubject<Prompt[]>([]);
  public prompts$ = this.promptsSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Get all admin users
   */
  getAdminUsers(): Observable<PromptResponse> {
    return this.http.get<PromptResponse>(`${environment.api}prompts/admin-users`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all prompts with optional filters and pagination
   */
  getAllPrompts(userId?: number | string, status?: number, page: number = 1, pageSize: number = 5): Observable<PromptResponse> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('pageSize', pageSize.toString());

    if (userId !== undefined) {
      params = params.set('user_id', userId.toString());
    }
    if (status !== undefined) {
      params = params.set('status', status.toString());
    }

    return this.http.get<PromptResponse>(this.apiUrl, { params }).pipe(
      tap(response => {
        if (Array.isArray(response.data) && response.data.length > 0) {
          // Type guard: check if it's an array of Prompts (has prompt_text property)
          if ('prompt_text' in response.data[0]) {
            this.promptsSubject.next(response.data as Prompt[]);
          }
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Get single prompt by ID
   */
  getPrompt(promptId: number): Observable<PromptResponse> {
    return this.http.get<PromptResponse>(`${this.apiUrl}/${promptId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Add new prompt
   */
  addPrompt(promptText: string, userId?: number, status: number = 1): Observable<PromptResponse> {
    const payload: any = {
      prompt_text: promptText,
      status
    };

    if (userId) {
      payload.user_id = userId;
    }

    return this.http.post<PromptResponse>(this.apiUrl, payload).pipe(
      tap(response => {
        if (response.success && response.data) {
          const currentPrompts = this.promptsSubject.value;
          this.promptsSubject.next([...currentPrompts, response.data as Prompt]);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update prompt
   */
  updatePrompt(promptId: number, promptText?: string, userId?: number, status?: number): Observable<PromptResponse> {
    const payload: any = {};

    if (promptText) {
      payload.prompt_text = promptText;
    }
    if (userId !== undefined) {
      payload.user_id = userId;
    }
    if (status !== undefined) {
      payload.status = status;
    }

    return this.http.put<PromptResponse>(`${this.apiUrl}/${promptId}`, payload).pipe(
      tap(response => {
        if (response.success) {
          const currentPrompts = this.promptsSubject.value;
          const updatedPrompts = currentPrompts.map(p =>
            p.id === promptId ? { ...p, ...payload } : p
          );
          this.promptsSubject.next(updatedPrompts);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete prompt
   */
  deletePrompt(promptId: number): Observable<PromptResponse> {
    return this.http.delete<PromptResponse>(`${this.apiUrl}/${promptId}`).pipe(
      tap(response => {
        if (response.success) {
          const currentPrompts = this.promptsSubject.value;
          const filteredPrompts = currentPrompts.filter(p => p.id !== promptId);
          this.promptsSubject.next(filteredPrompts);
        }
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: any) {
    console.error('Prompt service error:', error);
    return throwError(() => error);
  }
}
